import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, CheckCircle, MapPin, Clock, Award, Phone, Mail } from 'lucide-react';

function AASignsLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-yellow-400 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/aa-signs-logo.png" 
              alt="AA Signs Ltd Logo" 
              className="h-16 w-auto object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-black">AA SIGNS LTD</h1>
              <p className="text-sm text-gray-800">Professional Road Signage</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="tel:07411475878" className="flex items-center gap-2 text-black hover:text-gray-700">
              <Phone className="w-4 h-4" />
              <span className="font-semibold">07411 475 878</span>
            </a>
            <Button 
              onClick={() => navigate('/request-visit')} 
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Request Site Visit
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            Professional Road Signage for<br />House Builders Across the UK
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-blue-100">
            Fast Installation • UK-Wide Coverage • Decades of Experience
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={() => navigate('/request-visit')} 
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg px-8 py-6"
            >
              Request Site Visit <ArrowRight className="ml-2" />
            </Button>
            <div className="flex items-center gap-2 text-yellow-400">
              <Clock className="w-5 h-5" />
              <span className="font-semibold">3-4 Day Installation</span>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Banner */}
      <section className="bg-yellow-400 border-y-4 border-black py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
            <Award className="w-12 h-12 text-black" />
            <div>
              <h3 className="text-2xl font-bold text-black">The UK's Largest House Builder Sign Makers</h3>
              <p className="text-black">Leading the industry since the early 2000s with decades of signage experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-4xl font-bold text-center mb-12 text-gray-900">Our Services</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {/* AA Boards */}
            <Card className="border-2 border-yellow-400 hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-yellow-400 rounded-lg flex items-center justify-center mb-4 border-2 border-black">
                  <ArrowRight className="w-8 h-8 text-black" />
                </div>
                <h4 className="text-2xl font-bold mb-3 text-gray-900">Black and Yellow Directional Signs</h4>
                <p className="text-gray-600 mb-4">
                  Classic yellow directional signs with black text, arrows, and house logos. Perfect for guiding visitors to new housing developments.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Left, Right & Straight On options</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Weather-resistant materials</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Professional installation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Finger Boards */}
            <Card className="border-2 border-blue-600 hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-2xl font-bold mb-3 text-gray-900">Finger Boards</h4>
                <p className="text-gray-600 mb-4">
                  Directional signage placed on all major routes leading to your site from every direction.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Low coverage (25 signs)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Medium coverage (50 signs)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Heavy coverage (75 signs)</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Maintenance Visits */}
            <Card className="border-2 border-black hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-black rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-yellow-400" />
                </div>
                <h4 className="text-2xl font-bold mb-3 text-gray-900">Site Maintenance</h4>
                <p className="text-gray-600 mb-4">
                  Regular visits to ensure all your signs remain visible, upright, and effective.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">One-off visits available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Weekly, bi-weekly, or monthly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Keep signs standing strong</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-4xl font-bold text-center mb-12 text-gray-900">Why Choose AA Signs?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2 text-gray-900">Fast Turnaround</h4>
              <p className="text-gray-600">Signs made and installed within 3-4 days</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-black">
                <MapPin className="w-8 h-8 text-black" />
              </div>
              <h4 className="text-xl font-bold mb-2 text-gray-900">UK-Wide Coverage</h4>
              <p className="text-gray-600">Serving house builders across the entire UK</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-yellow-400" />
              </div>
              <h4 className="text-xl font-bold mb-2 text-gray-900">Proven Experience</h4>
              <p className="text-gray-600">Decades of expertise in signage solutions</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2 text-gray-900">Professional Service</h4>
              <p className="text-gray-600">Quality installation and maintenance</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-4xl font-bold mb-6">Ready to Get Started?</h3>
          <p className="text-xl mb-8 text-blue-100">
            Request a site visit today and let us handle your signage needs professionally.
          </p>
          <Button 
            onClick={() => navigate('/request-visit')} 
            size="lg"
            className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg px-8 py-6"
          >
            Request Site Visit Now <ArrowRight className="ml-2" />
          </Button>
          <p className="mt-6 text-sm text-blue-200">30-day payment terms on all accounts</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/aa-signs-logo.png" 
                  alt="AA Signs Ltd Logo" 
                  className="h-12 w-auto object-contain bg-white rounded-lg p-1"
                />
                <span className="text-xl font-bold">AA SIGNS LTD</span>
              </div>
              <p className="text-gray-400">Professional road signage for house builders across the UK since the early 2000s.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Black and Yellow Directional Signs</li>
                <li>Finger Board Installation</li>
                <li>Site Maintenance Visits</li>
                <li>UK-Wide Coverage</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Contact</h4>
              <div className="space-y-3">
                <a href="tel:07411475878" className="flex items-center gap-2 text-gray-400 hover:text-white">
                  <Phone className="w-4 h-4" />
                  <span>07411 475 878</span>
                </a>
                <a href="mailto:info@aasigns.co.uk" className="flex items-center gap-2 text-gray-400 hover:text-white">
                  <Mail className="w-4 h-4" />
                  <span>Email Us</span>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} AA SIGNS LTD. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AASignsLanding;
